import { useState, useEffect } from "react";
import {
  X, ChevronLeft, ChevronRight, Users, Activity,
  UserCheck, UserX, Building2, Loader2, ChevronDown,
} from "lucide-react";
import axiosInstance from "../../API/interceptor";
import TaskViewPanel from "../MyTask/TaskViewPanel";

const colorMap = [
  "#6366F1","#8B5CF6","#EC4899","#F59E0B","#10B981",
  "#3B82F6","#EF4444","#06B6D4","#84CC16","#F97316",
];


function initColor(n = "") {
  return colorMap[(n || "").charCodeAt(0) % colorMap.length];
}

// ─── Avatar ──────────────────────────────────────────────────────────────────
function Avatar({ name = "?", size = 36 }) {
  const ini = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{
        width: size,
        height: size,
        background: initColor(name),
        fontSize: size * 0.35,
      }}
    >
      {ini}
    </div>
  );
}

// ─── fmtTime ─────────────────────────────────────────────────────────────────
function fmtTime(v) {
  if (!v) return "Never";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  const now  = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60)     return "Just now";
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
}

// ─── DeptGroup ───────────────────────────────────────────────────────────────
function DeptGroup({ deptName, users, isActive, onViewTasks }) {
  const [open, setOpen] = useState(true);
  const deptColor = colorMap[deptName.charCodeAt(0) % colorMap.length];

  return (
    <div className="bg-white rounded-xl mb-3 overflow-hidden border-[1.5px] border-[#F1F5F9]">
      {/* Dept header */}
      <div
        onClick={() => setOpen(o => !o)}
        className={`flex items-center justify-between px-4 py-3 cursor-pointer bg-[#FAFBFE] hover:bg-[#F1F5F9] transition-colors duration-150 ${
          open ? "border-b border-[#F1F5F9]" : ""
        }`}
      >
        <div className="flex items-center gap-2.5">
          {/* Dept icon */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${deptColor}18` }}
          >
            <Building2 size={14} color={deptColor} />
          </div>

          <div>
            <div className="text-[13px] font-bold text-[#1E293B]">{deptName}</div>
            <div className="text-[11px] text-[#94A3B8]">
              {users.length} {users.length === 1 ? "user" : "users"}
            </div>
          </div>
        </div>

        <ChevronDown
          size={14}
          className={`text-[#94A3B8] transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}
        />
      </div>

      {/* Users list */}
      {open && (
        <div className="px-3 pt-2 pb-3">
          {users.map((user, i) => (
            <div
              key={user.user_id || i}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#F8FAFC] transition-colors duration-150 cursor-default"
            >
              {/* Avatar */}
              <Avatar name={user.user_name} size={36} />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1E293B] overflow-hidden whitespace-nowrap text-ellipsis">
                  {user.user_name || "Unknown"}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="flex items-center gap-[3px] text-[11px] text-[#94A3B8]">
                    <Activity size={10} />
                    {isActive ? fmtTime(user.last_activity) : "No recent activity"}
                  </span>
                </div>
              </div>

              {/* Task count */}
              <div className="text-center shrink-0">
                <div
                  className={`text-lg font-extrabold leading-none ${
                    isActive ? "text-[#059669]" : "text-[#94A3B8]"
                  }`}
                >
                  {user.task_count || 0}
                </div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">tasks</div>
              </div>

              {/* View tasks button */}
              {user.task_count > 0 && (
                <button
                  onClick={() => onViewTasks(user)}
                  className={`px-3 py-[5px] rounded-lg border-0 cursor-pointer text-[11px] font-semibold shrink-0 transition-all duration-150 ${
                    isActive
                      ? "bg-[#DCFCE7] text-[#059669] hover:bg-[#BBF7D0]"
                      : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"
                  }`}
                >
                  View Tasks
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ActiveUsersPanel ────────────────────────────────────────────────────
export default function ActiveUsersPanel({ open, status, filterBody, onClose }) {
  const [loading,      setLoading]      = useState(false);
  const [data,         setData]         = useState(null);
  const [taskListOpen, setTaskListOpen] = useState(false);
  const [taskListUser, setTaskListUser] = useState(null);
  const [tasks,        setTasks]        = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskId,       setTaskId]       = useState(null);

  const isActive = status === "active";
  const grad     = isActive
    ? "linear-gradient(135deg,#059669,#047857)"
    : "linear-gradient(135deg,#DC2626,#B91C1C)";
  const color    = isActive ? "#059669" : "#DC2626";

  useEffect(() => {
    if (!open || !status) return;
    setLoading(true); setData(null);
    const body = { ...(filterBody || {}), status };
    axiosInstance.post("/task_assignment/get_users_by_activity", body)
      .then(r => { if (r.data?.success) setData(r.data); })
      .catch(e => console.error("ActiveUsers:", e))
      .finally(() => setLoading(false));
  }, [open, status, JSON.stringify(filterBody)]);

  const handleViewTasks = (user) => {
    setTaskListUser(user);
    setTaskListOpen(true);
    setTasksLoading(true); setTasks([]);
    const body = {
      ...(filterBody || {}),
      status,
      filter_type:  "assigned_user",
      filter_value: String(user.user_id),
    };
    axiosInstance.post("/task_assignment/get_task_list_detail", body)
      .then(r => { if (r.data?.success) setTasks(r.data.data?.tasks || []); })
      .catch(e => console.error("UserTasks:", e))
      .finally(() => setTasksLoading(false));
  };

  const deptGroups = data?.department_groups || {};
  const totalUsers = data?.total_users || 0;
  const deptCount  = Object.keys(deptGroups).length;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-[1299] bg-[rgba(15,23,42,.35)] backdrop-blur-sm"
        />
      )}

      {/* Panel */}
      <div
        onClick={e => e.stopPropagation()}
        className={`fixed top-0 right-0 bottom-0 w-[min(900px,96vw)] bg-[#F8FAFC] z-[1300] flex flex-col transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Blur overlay when task list is open */}
        {taskListOpen && (
          <div className="absolute inset-0 bg-[rgba(15,23,42,.2)] backdrop-blur-[3px] z-10 pointer-events-none" />
        )}

        {/* ── Header ── */}
        <div className="shrink-0" style={{ background: grad }}>
          {/* Breadcrumb row */}
          <div className="flex items-center justify-between px-5 pt-[14px] pb-[10px]">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="flex items-center gap-1 bg-transparent border-0 cursor-pointer text-white/70 text-xs p-0"
              >
                <ChevronLeft size={14} /> Dashboard
              </button>
              <ChevronRight size={12} className="text-white/40" />
              <span className="text-white/70 text-xs">Active vs Inactive</span>
              <ChevronRight size={12} className="text-white/40" />
              <span className="text-white text-xs font-bold">
                {isActive ? "Active Users" : "Inactive Users"}
              </span>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[.12] border border-white/20 cursor-pointer"
            >
              <X size={14} className="text-white" />
            </button>
          </div>

          {/* Title + pills */}
          <div className="px-5 pb-[18px]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-[10px] bg-white/[.15] flex items-center justify-center">
                {isActive
                  ? <UserCheck size={20} className="text-white" />
                  : <UserX    size={20} className="text-white" />}
              </div>
              <div>
                <h1 className="text-white text-[18px] font-extrabold m-0 leading-tight">
                  {isActive ? "Active Users" : "Inactive Users"}
                </h1>
                <p className="text-white/65 text-xs m-0 mt-0.5">
                  {isActive
                    ? "Users who have recent activity in tasks"
                    : "Users assigned to tasks but no recent activity"}
                </p>
              </div>
            </div>

            {/* Summary pills */}
            <div className="flex gap-2 flex-wrap">
              <div className="flex items-center gap-[6px] px-3 py-[5px] rounded-lg bg-white/20 border border-white/30">
                <Users size={11} className="text-white" />
                <span className="text-[11px] font-semibold text-white">Total Users</span>
                <span className="text-[11px] font-extrabold px-[7px] py-px rounded-[5px] bg-white/25 text-white">
                  {totalUsers}
                </span>
              </div>
              <div className="flex items-center gap-[6px] px-3 py-[5px] rounded-lg bg-white/[.15] border border-white/25">
                <Building2 size={11} className="text-white" />
                <span className="text-[11px] font-semibold text-white">Departments</span>
                <span className="text-[11px] font-extrabold px-[7px] py-px rounded-[5px] bg-white/25 text-white">
                  {deptCount}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-[300px] gap-2.5">
              <Loader2 size={24} className="text-[#6366F1] animate-spin" />
              <span className="text-[13px] text-[#94A3B8]">Loading users...</span>
            </div>
          ) : !data || totalUsers === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] gap-3">
              {isActive
                ? <UserCheck size={48} className="text-[#CBD5E1]" />
                : <UserX    size={48} className="text-[#CBD5E1]" />}
              <span className="text-sm text-[#94A3B8]">
                No {isActive ? "active" : "inactive"} users found
              </span>
            </div>
          ) : (
            Object.entries(deptGroups).map(([deptName, users]) => (
              <DeptGroup
                key={deptName}
                deptName={deptName}
                users={users}
                isActive={isActive}
                onViewTasks={handleViewTasks}
              />
            ))
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-3 bg-[#F8FAFC] flex items-center justify-between shrink-0 border-t-[1.5px] border-[#F1F5F9]">
          <span className="text-xs text-[#94A3B8]">
            <strong className="text-[#374151]">{totalUsers}</strong>{" "}
            {isActive ? "active" : "inactive"} users across{" "}
            <strong className="text-[#374151]">{deptCount}</strong> departments
          </span>
          <button
            onClick={onClose}
            className="px-[22px] py-2 rounded-[10px] border-0 cursor-pointer text-white text-xs font-bold"
            style={{ background: grad, boxShadow: `0 3px 12px ${color}44` }}
          >
            Close
          </button>
        </div>
      </div>

      {/* ── Level 2 — Task List Panel ── */}
      {taskListOpen && (
        <>
          <div
            onClick={() => setTaskListOpen(false)}
            className="fixed inset-0 z-[1401] bg-[rgba(15,23,42,.25)] backdrop-blur-sm"
          />
          <div
            onClick={e => e.stopPropagation()}
            className="fixed top-0 right-0 bottom-0 w-[min(1200px,95vw)] bg-[#F8FAFC] z-[1402] flex flex-col animate-[slideInRight_.3s_cubic-bezier(.4,0,.2,1)]"
          >
            {/* Task list header */}
            <div className="shrink-0 px-5 pt-[14px] pb-4" style={{ background: grad }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTaskListOpen(false)}
                    className="flex items-center gap-1 bg-transparent border-0 cursor-pointer text-white/70 text-xs p-0"
                  >
                    <ChevronLeft size={14} /> Back
                  </button>
                  <ChevronRight size={12} className="text-white/40" />
                  <span className="text-white text-[13px] font-bold">
                    {taskListUser?.user_name} — Tasks
                  </span>
                </div>
                <button
                  onClick={() => setTaskListOpen(false)}
                  className="w-[30px] h-[30px] rounded-lg flex items-center justify-center bg-white/[.12] border border-white/20 cursor-pointer"
                >
                  <X size={13} className="text-white" />
                </button>
              </div>
              <div className="text-sm text-white/80">
                {taskListUser?.task_count || 0} tasks assigned to {taskListUser?.user_name}
              </div>
            </div>

            {/* Task list body */}
            <div className="flex-1 overflow-y-auto p-5">
              {tasksLoading ? (
                <div className="flex items-center justify-center h-[200px] gap-2.5">
                  <Loader2 size={20} className="text-[#6366F1] animate-spin" />
                  <span className="text-[13px] text-[#94A3B8]">Loading tasks...</span>
                </div>
              ) : (
                <div className="rounded-[10px] border border-[#F1F5F9] overflow-hidden">
                  <table className="w-full border-separate border-spacing-0 text-xs">
                    <thead>
                      <tr>
                        {["TASK ID","TASK NAME","PRIORITY","STATUS","WORKSPACE","DEADLINE"].map(h => (
                          <th
                            key={h}
                            className="px-3 py-[10px] text-left text-[10px] font-bold text-[#94A3B8] tracking-[.8px] uppercase bg-[#F8FAFC] border-b-2 border-[#F1F5F9] whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-10 text-center text-[#CBD5E1] text-[13px]">
                            No tasks found
                          </td>
                        </tr>
                      ) : tasks.map((t, i) => {
                        const rowBg = i % 2 === 0 ? "bg-white" : "bg-[#FAFBFE]";
                        const tdCls = `px-3 py-[10px] border-b border-[#F1F5F9] ${rowBg}`;
                        return (
                          <tr key={t._id || i}>
                            <td className={tdCls}>
                              <span className="font-mono text-[11px] bg-[#EEF2FF] text-[#4338CA] px-[6px] py-[2px] rounded-[5px]">
                                #{t.unique_seq_id || "—"}
                              </span>
                            </td>
                            <td className={tdCls}>
                              <span
                                onClick={() => setTaskId(t._id)}
                                className="text-[#4F46E5] font-semibold cursor-pointer max-w-[200px] overflow-hidden text-ellipsis block whitespace-nowrap hover:underline"
                              >
                                {t.task_type || "—"}
                              </span>
                            </td>
                            <td className={tdCls}>
                              <span className="text-[11px] font-bold px-2 py-[2px] rounded-full bg-[#FEF9C3] text-[#854D0E]">
                                {t.priority || "—"}
                              </span>
                            </td>
                            <td className={tdCls}>
                              <span className="text-[11px] px-2 py-[2px] rounded-full bg-[#EEF2FF] text-[#4338CA]">
                                {t.task_status || "—"}
                              </span>
                            </td>
                            <td className={tdCls}>
                              <span className="text-[11px] bg-[#F1F5F9] text-[#475569] px-2 py-[2px] rounded-[6px]">
                                {t.product_name || "—"}
                              </span>
                            </td>
                            <td className={tdCls}>
                              <span className="text-[11px] text-[#374151]">
                                {t.completion_sla
                                  ? new Date(t.completion_sla).toLocaleDateString()
                                  : "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Level 3 — Task Detail ── */}
      <TaskViewPanel taskId={taskId} onClose={() => setTaskId(null)} />

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}